import moment from 'moment';

import {ProjectFeatureFlag} from 'common/report/constants';
import {featureSamplingUtils} from 'common/report/util/feature-sampling';
import {Agreement, AgreementLine, BuyableId, BuyableType} from 'common/types/app-api/billing_bump/types';
import {LocalizationStore} from 'common/i18n/store';
import {DEFAULT_REPLAY_RETENTION_DAYS} from 'common/widgets/recording-player/query-utils';

import {getEarliestStartAndLatestEndDate, sortAgreementLines} from './billing-info-page/utils';

const ENTERPRISE_BUYABLE_IDS = [BuyableId.ENTERPRISE_PLAN, BuyableId.ENTERPRISE_PLAN_WITH_PROFILES];

export enum PlanType {
  Free = `free`,
  Growth = `growth`,
  Enterprise = `enterprise`,
  Startup = `startup`,
  Unknown = `unknown`,
}

export function eligibleForNewPlanBuilderFeature(organizationId: string, orgFeatureFlags?: string[]): boolean {
  // Use new plan builder if
  // 1. Organization was manually assigned the feature flag in Django
  // 2. Organiztion is part of the feature roll out sample group (currently set to 100% - updated on May 29th)
  const CURRENT_ROLLOUT_PERCENTAGE = 100;
  const orgFeatureFlagsSet = new Set(orgFeatureFlags);
  return (
    orgFeatureFlagsSet.has(ProjectFeatureFlag.InProductPlanBuilder) ||
    featureSamplingUtils.determineFeatureEligibility(
      organizationId,
      ProjectFeatureFlag.InProductPlanBuilder,
      CURRENT_ROLLOUT_PERCENTAGE,
    )
  );
}

export function frequencyFromTermLength(i18n: LocalizationStore, frequency: number = 1): string {
  if (frequency === 1) {
    return i18n.t(`settings.org-settings.plan-details-new.monthly`);
  }
  return i18n.t(`settings.org-settings.plan-details-new.annual`);
}

/**
 * Parse a list of agreements
 * @param agreements a list of agreements
 * @returns {object} return a record of records structured in the following way
 *                   buyable_id: { end_date: { other information} }
 *                   assume that if a two agreement lines have the same buyable_id and end_date then the are deltas
 *                   otherwise assume that the line items are for two separate terms
 */
function getBuyableInformationFromAgreements(
  agreements: Agreement[],
): Record<string, Record<string, Record<string, TODO>>> {
  const sortedLines: AgreementLine[] = agreements
    .sort((a, b) => sortAgreementLines(a, b))
    .reduce((lines: AgreementLine[], agreement) => lines.concat(agreement.agreement_lines), []);
  const parsedItems: Record<string, Record<string, TODO>> = {};

  sortedLines.forEach((item: AgreementLine) => {
    if (!parsedItems[item.buyable_id]) {
      parsedItems[item.buyable_id] = {};
      parsedItems[item.buyable_id][item.end_date] = {...item};
    } else if (!parsedItems[item.buyable_id][item.end_date]) {
      parsedItems[item.buyable_id][item.end_date] = {...item};
    } else {
      parsedItems[item.buyable_id][item.end_date].net_price = (
        Number(parsedItems[item.buyable_id][item.end_date].net_price) + Number(item.net_price)
      ).toString();
      parsedItems[item.buyable_id][item.end_date].quantity += item.quantity;
    }

    // Always take the lowest custom overage rate
    if (
      item.custom_overage_rate &&
      item.custom_overage_rate < parsedItems[item.buyable_id][item.end_date].custom_overage_rate
    ) {
      parsedItems[item.buyable_id][item.end_date].custom_overage_rate = item.custom_overage_rate;
    }
  });

  return parsedItems;
}

/**
 * Get the (only) currently active set of line items for a buyableId
 * @param currentItemInformationDict a record of all current the line items for a buyableId sectioned by start/end date
 * @returns {object | null} the information for the currently active line items, if none, return null
 */
function getCurrentItemInformation(
  currentItemInformationDict: Record<string, Record<string, TODO>>,
  today: Date,
): Record<string, TODO> | null {
  const sortedKeys = Object.keys(currentItemInformationDict).sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime(),
  );
  for (const keyDate of sortedKeys) {
    if (
      moment.utc(today).isSameOrBefore(moment.utc(keyDate), `day`) &&
      moment.utc(today).isSameOrAfter(moment.utc(currentItemInformationDict[keyDate].start_date), `day`)
    ) {
      return currentItemInformationDict[keyDate];
    }
  }

  return null;
}

/**
 * Get the (only) upcoming set of line items for a buyableId
 * @param currentItemInformationDict a record of all the current line items for a buyableId sectioned by start/end date
 * @param upcomingItemInformationDict a record of all the upcoming line items for a buyableId sectioned by start/end date
 * @param upcomingTermStartDate the start date of the upcoming term
 * @returns {object | null} the information for the currently active line items, if none, return null
 */
function getUpcomingItemInformation(
  currentItemInformationDict: Record<string, Record<string, TODO>>,
  upcomingItemInformationDict: Record<string, Record<string, TODO>>,
  upcomingTermStartDate: string,
): Record<string, TODO> | null {
  // First check the current line items -- take precedent over renewal line items
  for (const currentItemDate of Object.keys(currentItemInformationDict)) {
    if (currentItemInformationDict[currentItemDate].start_date === upcomingTermStartDate) {
      return currentItemInformationDict[currentItemDate];
    }
  }

  // Then check the renewal line items
  for (const upcomingItemDate of Object.keys(upcomingItemInformationDict)) {
    if (upcomingItemInformationDict[upcomingItemDate].start_date === upcomingTermStartDate) {
      return upcomingItemInformationDict[upcomingItemDate];
    }
  }

  return null;
}

export type BuyableInformation = {
  buyableId: BuyableId;
  buyableType: BuyableType;
  buyableName: string;
  netPrice: string;
  bucketMonths?: number;
  startDate: string;
  endDate: string;
  potentialChangeDate: string;
  changeHappening: boolean;
  currentTermLengthInMonths: number;
  currentQuantity: number;
  upcomingTermLengthInMonths?: number;
  upcomingQuantity?: number;
  customOverageRate?: string;
  retentionDays?: number;
};

/**
 * Parse current and upcoming agreements for information on buyables
 * @param currentAgreements all current agreements in effect
 * @param upcomingAgreements all upcoming agreements
 * @param today todays date
 * @returns {object} buyable, quantity, term-length, renewal information on buyables
 */
export function parseAgreements(
  planType: PlanType,
  currentAgreements: Agreement[] = [],
  upcomingAgreements: Agreement[] = [],
  today: Date = new Date(),
): Record<number, BuyableInformation> {
  const {latestEndDate} = getEarliestStartAndLatestEndDate(currentAgreements);
  const filteredUpcomingAgreements =
    upcomingAgreements.filter((agreement: Agreement) =>
      moment.utc(agreement.start_date).isSame(moment.utc(latestEndDate || ``).add(1, `days`), `day`),
    ) || [];

  const currentItems = getBuyableInformationFromAgreements(currentAgreements);
  const upcomingItems = getBuyableInformationFromAgreements(filteredUpcomingAgreements);

  const buyableInformation: Record<number, BuyableInformation> = {};
  for (const currentItem in currentItems) {
    const buyableId: number = Number(currentItem);
    const currentItemInformationDict = currentItems[currentItem];
    const upcomingItemInformationDict = upcomingItems[currentItem] || {};

    const currentItemInformation = getCurrentItemInformation(currentItemInformationDict, today);

    if (!currentItemInformation) {
      continue;
    }

    const upcomingTermStartDate = moment.utc(currentItemInformation.end_date).add(1, `days`).format(`YYYY-MM-DD`);
    const upcomingItemInformation = getUpcomingItemInformation(
      currentItemInformationDict,
      upcomingItemInformationDict,
      upcomingTermStartDate,
    );

    const buyableInfo: BuyableInformation = {
      buyableId,
      buyableType: currentItemInformation.buyable_type,
      buyableName: currentItemInformation.buyable_name,
      netPrice: currentItemInformation.net_price,
      currentTermLengthInMonths: currentItemInformation.term_length_in_months,
      currentQuantity: currentItemInformation.quantity,
      customOverageRate: currentItemInformation.custom_overage_rate,
      startDate: currentItemInformation.start_date,
      endDate: currentItemInformation.end_date,
      changeHappening: false,
      // This will always be one day after the end date of this line
      potentialChangeDate: upcomingTermStartDate,
    };

    // If the current plan is free then plan can't be downgraded
    // If the buyableType is base feature plan
    if (buyableId !== BuyableId.FREE_PLAN && buyableInfo.buyableType === BuyableType.BASE_FEATURE_PLAN) {
      // If the current plan is an enterprise trial, check to see if the upcoming plan is an enterprise plan
      // If not, then show that a change is happening
      if (buyableId === BuyableId.ENTERPRISE_TRIAL_PLAN) {
        const upcomingEnterprisePlanBuyableId = Object.keys(upcomingItems).filter((upcomingBuyableId) =>
          ENTERPRISE_BUYABLE_IDS.includes(parseInt(upcomingBuyableId)),
        );

        if (upcomingEnterprisePlanBuyableId.length !== 1 || !upcomingItems[upcomingEnterprisePlanBuyableId[0]]) {
          buyableInfo.changeHappening = true;
        }
        // If the current plan is an startup plan, check to see if the upcoming plan is a free plan
        // If it is, then show that a change is happening
      } else if (buyableId === BuyableId.ENTERPRISE_STARTUP_PLAN_BUYABLE_ID) {
        const isFreePlanIncoming = Object.keys(upcomingItems).some(
          (upcomingBuyableId) => parseInt(upcomingBuyableId) === BuyableId.FREE_PLAN,
        );

        if (isFreePlanIncoming) {
          buyableInfo.changeHappening = true;
        }
        // check if the same buyable exists within the upcoming agreements or if it should autorenew
      } else if (!upcomingItemInformation && !currentItemInformation.metadata?.should_autorenew) {
        buyableInfo.changeHappening = true;
      }
    }

    if (buyableId === BuyableId.MTUS || buyableId === BuyableId.SESSION_REPLAY) {
      buyableInfo.bucketMonths = currentItemInformation.metadata?.bucket_months;
    }

    if (buyableId === BuyableId.SESSION_REPLAY_RETENTION_PERIOD) {
      buyableInfo.retentionDays = currentItemInformation.metadata?.retention_days || DEFAULT_REPLAY_RETENTION_DAYS;
    }

    // New quantity is 1_000_000 for events since that's the lowest
    // 10_000 for session replays since that's the lowest
    // otherwise 0
    let newQuantity = 0;
    if (buyableId === BuyableId.EVENTS) {
      newQuantity = 1_000_000;
    }
    if (buyableId === BuyableId.SESSION_REPLAY) {
      // If the plan is free they get 10_000 free session replays,
      // otherwise every other plan gets 20_000
      if (planType === PlanType.Free) {
        newQuantity = 10_000;
      } else {
        newQuantity = 20_000;
      }
    }
    // If the buyableType is metered, check if the amount is decreasing or if it should auto-renew
    // There should always be an auto-renew or upcoming line item
    if (buyableInfo.buyableType === BuyableType.METERED && planType !== PlanType.Free) {
      const quantityWillChange = currentItemInformation.quantity !== (upcomingItemInformation?.quantity || newQuantity);
      // If there is upcoming information then compare the quantities
      if (upcomingItemInformation) {
        if (quantityWillChange) {
          buyableInfo.changeHappening = true;
          buyableInfo.upcomingTermLengthInMonths = upcomingItemInformation?.term_length_in_months || 1;
          buyableInfo.upcomingQuantity = upcomingItemInformation?.quantity || newQuantity;
        }
      } else if (currentItemInformation && !currentItemInformation.metadata?.should_autorenew) {
        // Case where there is no upcoming item information and the item should not autorenew
        // Should never happen but handle the case
        buyableInfo.changeHappening = true;
        buyableInfo.upcomingTermLengthInMonths = 1;
        buyableInfo.upcomingQuantity = newQuantity;
      }
    }

    // If the buyableType is an add on, check if it exists in the upcoming agreement or if it should autorenew
    if (buyableInfo.buyableType === BuyableType.ADDON) {
      // If there is no upcoming item information and the plan type is growth then show a change
      if (!upcomingItemInformation && planType === PlanType.Growth) {
        buyableInfo.changeHappening = true;
        // Otherwise, the plan is enterprise and check if there is an autorenew in the metadata
        // eslint-disable-next-line sonarjs/no-duplicated-branches
      } else if (!currentItemInformation.metadata?.should_autorenew && planType === PlanType.Enterprise) {
        buyableInfo.changeHappening = true;
      }
    }

    // Otherwise if buyableType is a discount or support add ons, do nothing (even if it is going away)

    buyableInformation[buyableId] = buyableInfo;
  }

  return buyableInformation;
}

export function aggregateTalliesByMonth(tallies: any[]): any[] {
  const aggregatedMap = new Map<string, any>();
  for (const tally of tallies) {
    const [year, month] = tally.date.split(`-`);
    const key = `${tally.project}-${year}-${month}`;

    if (!aggregatedMap.has(key)) {
      aggregatedMap.set(key, {
        project: tally.project,
        // The date will be the first day of the month to match
        date: `${year}-${month}-01`,
        total: 0,
        adjustment: 0,
        usage: 0,
      });
    }

    const aggregatedTally = aggregatedMap.get(key);
    aggregatedTally.total += tally.total;
    aggregatedTally.adjustment += tally.adjustment;
    aggregatedTally.usage += tally.usage;
  }

  return Array.from(aggregatedMap.values());
}
